// #28 Disclosure: a collapsible section heading that is a real button, with aria-expanded and aria-controls; the
// panel stays in the DOM (hidden when closed), so the aria-controls target always exists. Controlled (open +
// onOpenChange) or uncontrolled (defaultOpen).

import type { ReactNode } from "react";
import { cx } from "../lib/cx";
import { useHs, type Hs } from "../lib/hs";
import { useDisclosure } from "../lib/useDisclosure";
import { Icon } from "./icons";

export interface DisclosureProps {
  /** The heading's words (*Error details*, *Conflicts*, *Revise own score* …). */
  title: ReactNode;
  /** A meta line beside the heading. */
  sub?: ReactNode | undefined;
  /** Something on the row's right (a count, a pill). */
  right?: ReactNode | undefined;
  children: ReactNode;
  open?: boolean | undefined;
  defaultOpen?: boolean | undefined;
  onOpenChange?: ((open: boolean) => void) | undefined;
  /** The panel's id (default generated). */
  id?: string | undefined;
  /** The compact heading (13 px, meta weight): inside a strip or a dense card. */
  sm?: boolean | undefined;
  can?: boolean | undefined;
  hs?: Hs | undefined;
  className?: string | undefined;
}

export function Disclosure({ title, sub, right, children, open, defaultOpen, onOpenChange, id, sm, can, hs, className }: DisclosureProps) {
  const hot = useHs(hs);
  const state = useDisclosure({ open, defaultOpen, onOpenChange, id });
  if (can === false) return null;
  return (
    <div className={cx("mc-disc", sm && "mc-disc-sm", className)}>
      <div className="mc-disc-row">
        <button type="button" className="mc-disc-h" {...state.button} {...hot}>
          <Icon name={state.open ? "down" : "right"} size={sm ? 16 : 18} />
          <span className="mc-disc-t">{title}</span>
        </button>
        {sub ? <span className="mc-disc-s">{sub}</span> : null}
        {right ? <span className="mc-disc-r">{right}</span> : null}
      </div>
      <div className="mc-disc-b" id={state.panel.id} hidden={state.panel.hidden}>{children}</div>
    </div>
  );
}
